import { useEffect, useRef, useState } from "react";
import { socketUrl } from "./api";
import type { RoomPublicState, ServerMessage } from "../shared/types";

export function useRoomSocket(path: string | null, initial?: RoomPublicState) {
  const [state, setState] = useState<RoomPublicState | undefined>(initial);
  const [connected, setConnected] = useState(false);
  const [message, setMessage] = useState<string>();
  const socketRef = useRef<WebSocket | undefined>(undefined);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    let retry = 500;
    let reconnectTimer: number | undefined;
    const connect = () => {
      if (cancelled) return;
      const socket = new WebSocket(socketUrl(path));
      socketRef.current = socket;
      socket.addEventListener("open", () => { setConnected(true); retry = 500; });
      socket.addEventListener("message", (event) => {
        try {
          const parsed = JSON.parse(String(event.data)) as ServerMessage;
          if (parsed.type === "state") { setState(parsed.state); setMessage(undefined); }
          else setMessage(parsed.message);
        } catch { setMessage("A realtime update could not be read."); }
      });
      socket.addEventListener("close", () => {
        setConnected(false);
        if (!cancelled) {
          reconnectTimer = window.setTimeout(connect, retry);
          retry = Math.min(retry * 2, 8_000);
        }
      });
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      socketRef.current?.close();
    };
  }, [path]);

  return {
    state,
    setState,
    connected,
    message,
    send: (payload: unknown) => {
      if (socketRef.current?.readyState !== WebSocket.OPEN) { setMessage("Reconnecting… please try again."); return false; }
      socketRef.current.send(JSON.stringify(payload));
      return true;
    },
  };
}

export function useNow(active = true): number {
  const [now, setNow] = useState(0);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}
